import { getSumsubRejectLabel } from "@repo/shared";
import type { SumsubApplicantDataResult } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./sumsub-results.i18n";

type SumsubResultsProps = {
  lang: string;
  applicantData: SumsubApplicantDataResult["data"] | null;
};

export function SumsubResults({ lang, applicantData }: SumsubResultsProps) {
  const t = useT(T);
  if (!applicantData) {
    return null;
  }

  const isVerified = applicantData.reviewResult?.reviewAnswer === "GREEN";
  const fullName = [applicantData.info?.firstName, applicantData.info?.lastName]
    .filter(Boolean)
    .join(" ");
  const dob = applicantData.info?.dob;
  const country = applicantData.info?.country;
  const expiryDate = applicantData.documents?.[0]?.expiryDate;

  return (
    <div className="space-y-3">
      {/* Status badges - using same colors as PEP */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t.status}:</span>
          <Badge variant={isVerified ? "success" : "destructive"}>
            {isVerified ? t.verified : t.notVerified}
          </Badge>
        </div>
      </div>

      {/* Info row */}
      {(fullName || dob || country || expiryDate) && (
        <div className="border rounded-lg px-4 py-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
            {fullName && (
              <span>
                {t.nameLabel} <span className="text-foreground">{fullName}</span>
              </span>
            )}
            {dob && (
              <span>
                {t.birthLabel} <span className="text-foreground">{dob}</span>
              </span>
            )}
            {country && (
              <span>
                {t.countryLabel} <span className="text-foreground">{country}</span>
              </span>
            )}
            {expiryDate && (
              <span>
                {t.expiresLabel} <span className="text-foreground">{expiryDate}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Reject reasons if any */}
      {applicantData.reviewResult?.rejectLabels &&
        applicantData.reviewResult.rejectLabels.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-destructive">{t.rejectReasons}</span>
            <ul className="mt-1 list-disc list-inside text-muted-foreground">
              {applicantData.reviewResult.rejectLabels.map((label) => (
                <li key={label}>{getSumsubRejectLabel(label, lang)}</li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
}
